import { useState } from "react";
import { Tag, Check, X, Globe } from "lucide-react";
import { apiCall } from "../App";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const CURRENCIES = [
  { code: "INR", symbol: "\u20B9", label: "INR" },
  { code: "USD", symbol: "$", label: "USD" },
];

export const CurrencySelector = ({ value, onChange }) => (
  <div>
    <p className="text-chakra-crown/50 text-xs font-heading tracking-wider mb-2 flex items-center gap-1">
      <Globe className="w-3 h-3" /> CURRENCY
    </p>
    <div className="flex gap-2">
      {CURRENCIES.map(c => (
        <button
          key={c.code}
          onClick={() => onChange(c.code)}
          data-testid={`currency-${c.code.toLowerCase()}`}
          className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
            value === c.code
              ? "border-divine-gold bg-divine-gold/10 text-divine-gold"
              : "border-divine-gold/10 text-chakra-crown/50 hover:border-divine-gold/30"
          }`}
        >
          {c.symbol} {c.label}
        </button>
      ))}
    </div>
  </div>
);

export const CouponInput = ({ token, module, currency, orderType, onApply, onClear }) => {
  const [code, setCode] = useState("");
  const [validating, setValidating] = useState(false);
  const [applied, setApplied] = useState(null);
  const [error, setError] = useState("");

  const handleValidate = async () => {
    if (!code.trim()) return;
    setValidating(true);
    setError("");
    const res = await apiCall("POST", "/subscription/coupons/validate", {
      code: code.trim(),
      module,
      currency,
      order_type: orderType,
    }, token);
    setValidating(false);
    if (res.success) {
      setApplied(res.data);
      onApply(code.trim().toUpperCase(), res.data);
    } else {
      setError(res.error || "Invalid coupon");
      setApplied(null);
      onClear();
    }
  };

  const handleClear = () => {
    setCode("");
    setApplied(null);
    setError("");
    onClear();
  };

  return (
    <div data-testid="coupon-section">
      <p className="text-chakra-crown/50 text-xs font-heading tracking-wider mb-2 flex items-center gap-1">
        <Tag className="w-3 h-3" /> DISCOUNT COUPON
      </p>
      {applied ? (
        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <Check className="w-4 h-4 text-emerald-400" />
          <span className="text-emerald-400 text-sm font-medium">{applied.code}</span>
          <span className="text-emerald-400/70 text-xs">
            {applied.discount_type === "percentage" ? `${applied.discount_value}% off` : `${currency === "USD" ? "$" : "\u20B9"}${applied.discount_value} off`}
          </span>
          <button onClick={handleClear} className="ml-auto text-chakra-crown/40 hover:text-red-400">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Input
            value={code}
            onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(""); }}
            placeholder="Enter coupon code"
            className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown text-sm"
            data-testid="coupon-input"
          />
          <Button
            size="sm"
            onClick={handleValidate}
            disabled={validating || !code.trim()}
            data-testid="apply-coupon-btn"
            className="bg-divine-gold/20 text-divine-gold hover:bg-divine-gold/30 border border-divine-gold/30 px-4"
          >
            {validating ? "..." : "Apply"}
          </Button>
        </div>
      )}
      {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
};
